import { Link } from 'react-router-dom'

import { agentPath, taskPath } from '../observability/routing'
import { useConsoleView } from '../observability/state'

function healthValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return JSON.stringify(value)
}

export function Overview() {
  const { snapshot, connectionStatus } = useConsoleView()
  const { instance, agents, tasks, contexts, communications } = snapshot
  const activeAgents = agents.filter((agent) => agent.status === 'active').length
  const blockedTasks = tasks.filter((task) => task.operationalStatus === 'blocked').length
  const alerts = communications.reduce((total, item) => total + item.alertsPending, 0)

  return (
    <section className="stack" aria-labelledby="overview-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Connected instance</p>
          <h2 id="overview-title">{instance.application}</h2>
          <p className="muted">{instance.publicBaseUrl}</p>
        </div>
        <span className={`status status-${instance.healthy ? 'healthy' : 'offline'}`}>
          {instance.healthy ? 'healthy' : 'unhealthy'}
        </span>
      </div>

      <div className="metric-grid metric-grid-four">
        <article className="card"><span>Version</span><strong>{instance.version}</strong></article>
        <article className="card"><span>Active agents</span><strong>{activeAgents}</strong></article>
        <article className="card"><span>Blocked tasks</span><strong>{blockedTasks}</strong></article>
        <article className="card"><span>Pending alerts</span><strong>{alerts}</strong></article>
      </div>

      <div className="two-column">
        <article className="panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Connection</p>
              <h3>Snapshot + realtime state</h3>
            </div>
            <span className={`status status-${connectionStatus}`}>{connectionStatus}</span>
          </div>
          <dl className="detail-list">
            <div><dt>High-water sequence</dt><dd>{snapshot.highWaterSeq}</dd></div>
            <div><dt>Replay from</dt><dd>{snapshot.replayFromSeq}</dd></div>
            <div><dt>Duplicate events</dt><dd>{snapshot.duplicateEventsPossible ? 'possible' : 'deduplicated'}</dd></div>
            <div><dt>Context entries</dt><dd>{contexts.length}</dd></div>
          </dl>
        </article>

        <article className="panel">
          <div>
            <p className="eyebrow">Health</p>
            <h3>Runtime signals</h3>
          </div>
          <dl className="detail-list">
            {Object.entries(instance.health).map(([key, value]) => (
              <div key={key}><dt>{key.replaceAll('_', ' ')}</dt><dd>{healthValue(value)}</dd></div>
            ))}
          </dl>
        </article>
      </div>

      <article className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Attention</p>
            <h3>Current operational pressure</h3>
          </div>
          {agents[0] && (
            <Link className="text-link" to={agentPath(instance.publicBaseUrl, agents[0].name)}>
              Open agents
            </Link>
          )}
        </div>
        <div className="feed">
          {communications.filter((item) => item.alertsPending > 0).map((item) => (
            <div className="feed-row" key={item.name}>
              <div>
                <strong>{item.name}</strong>
                <p className="muted">{item.alertsPending} alert · {item.messagesAwaitingReply} reply required</p>
              </div>
              <Link className="text-link" to={agentPath(instance.publicBaseUrl, item.name)}>Inspect</Link>
            </div>
          ))}
          {tasks.filter((task) => task.priority === 'P0' || task.priority === 'P1').map((task) => (
            <div className="feed-row" key={task.key}>
              <div>
                <strong>{task.taskId} · {task.title}</strong>
                <p className="muted">{task.operationalStatus} · {task.nextAction}</p>
              </div>
              <Link className="text-link" to={taskPath(instance.publicBaseUrl, task.namespace, task.taskId)}>Inspect</Link>
            </div>
          ))}
          {alerts === 0 && <p className="muted">No pending alerts.</p>}
        </div>
      </article>
    </section>
  )
}
